import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { Avatar, AvatarGroup, Tab, Tabs } from "@mui/material";
import FilterAutocomplete from "components/FilterAutocomplete";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/MUI";

// Data & utilities
import axios from "axios";
import { withAuthContext } from "context/Auth";
import { useNavigate } from "react-router-dom";
import breakpoints from "assets/theme/base/breakpoints";
import {
	AccessTimeFilled,
	Cancel,
	CheckCircle,
	ManageAccountsOutlined,
} from "@mui/icons-material";
import AdminIcon from "assets/images/bg-profile.jpeg";
import ImageCloud from "../../link";
import moment from "moment";

/* --------------------
	 Utility
-------------------- */
function daysPassed(startDate, endDate) {
	const start = new Date(startDate);
	const end = new Date(endDate);
	return (end - start) / (1000 * 60 * 60 * 24);
}

/* --------------------
	 Extracted & memoized table component
-------------------- */
const ServiceTableComponent = React.memo(function ServiceTableComponent({
	data,
	error,
	tabValue,
	columns,
	setTabValue,
	navigate,
	isLoading,
	pagination,
	currentPage,
	itemsPerPage,
	handlePageChange,
	filters,
	filterOptions,
	onFilterChange,
	onClearFilters,
	isFilterLoading,
	filterError
}) {
	const [tabsOrientation, setTabsOrientation] = useState("horizontal");

	useEffect(() => {
		function handleTabsOrientation() {
			setTabsOrientation(
				window.innerWidth < breakpoints.values.sm ? "vertical" : "horizontal"
			);
		}

		window.addEventListener("resize", handleTabsOrientation);
		handleTabsOrientation();

		return () => {
			window.removeEventListener("resize", handleTabsOrientation);
		};
	}, []);

	const handleSetTabValue = useCallback((event, newValue) => {
		setTabValue(newValue);
	}, [setTabValue]);

	const onNewClick = useCallback(() => navigate(`/SelectServiceOffered/New`), [navigate]);

	const statusOptions = useMemo(() => filterOptions?.statuses || [], [filterOptions?.statuses]);
	const serviceOfferingOptions = useMemo(
		() => filterOptions?.serviceOfferings || [],
		[filterOptions?.serviceOfferings]
	);
	const adminOptions = useMemo(() => filterOptions?.admins || [], [filterOptions?.admins]);
	const propertyOptions = useMemo(
		() => filterOptions?.properties || [],
		[filterOptions?.properties]
	);

	const serviceOfferingLabel = useCallback((option) => option?.title || "Untitled", []);
	const adminLabel = useCallback((option) => option?.name || option?.email || "Unnamed", []);
	const propertyLabel = useCallback((option) => {
		const tower = option?.Tower || "";
		const flat = option?.FlatNo || "";
		const combined = `${tower} ${flat}`.trim();
		return combined || option?.description || option?._id || "Property";
	}, []);

	return (
		<Card>
			{/* Header with title + New button */}
			<MDBox
				mx={2}
				mt={-3}
				py={3}
				px={2}
				variant="gradient"
				bgColor="info"
				borderRadius="lg"
				coloredShadow="info"
			>
				<Grid container style={{ width: "100%" }}>
					<Grid item xs={6}>
						<MDTypography variant="h6" color="white">
							{tabValue} Service
						</MDTypography>
					</Grid>
					<Grid
						item
						xs={6}
						style={{
							display: "flex",
							alignItems: "flex-end",
							justifyContent: "flex-end",
						}}
					>
						<MDBadge
							badgeContent="New"
							color="dark"
							variant="gradient"
							size="sm"
							onClick={onNewClick}
						/>
					</Grid>
				</Grid>
			</MDBox>

			{/* Filters */}
			<MDBox px={2} py={2} mt={2} bgcolor="rgba(255,255,255,0.8)" borderRadius="md">
				<Grid container spacing={2} alignItems="center">
					<Grid item xs={12} sm={6} md={3}>
						<FilterAutocomplete
							name="status"
							label="Status"
							placeholder="All statuses"
							value={filters.status}
							options={statusOptions}
							onChange={onFilterChange}
							loading={isFilterLoading}
							disabled={isFilterLoading}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<FilterAutocomplete
							name="serviceOffering"
							label="Service Offering"
							placeholder="All offerings"
							value={filters.serviceOffering}
							options={serviceOfferingOptions}
							onChange={onFilterChange}
							loading={isFilterLoading}
							disabled={isFilterLoading}
							getOptionLabel={serviceOfferingLabel}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<FilterAutocomplete
							name="admin"
							label="Admin"
							placeholder="All admins"
							value={filters.admin}
							options={adminOptions}
							onChange={onFilterChange}
							loading={isFilterLoading}
							disabled={isFilterLoading}
							getOptionLabel={adminLabel}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<FilterAutocomplete
							name="property"
							label="Property"
							placeholder="All properties"
							value={filters.property}
							options={propertyOptions}
							onChange={onFilterChange}
							loading={isFilterLoading}
							disabled={isFilterLoading}
							getOptionLabel={propertyLabel}
						/>
					</Grid>
					<Grid item xs={12} display="flex" justifyContent="flex-end">
						<MDButton
							variant="outlined"
							color="info"
							size="small"
							disabled={isFilterLoading}
							onClick={onClearFilters}
						>
							Clear filters
						</MDButton>
					</Grid>
				</Grid>
				{filterError && (
					<MDTypography variant="caption" color="error">
						{filterError}
					</MDTypography>
				)}
			</MDBox>

			{/* Tabs */}
			<MDBox
				mx={2}
				mt={-3}
				py={3}
				px={2}
				variant="gradient"
				bgColor="info"
				borderRadius="lg"
				coloredShadow="info"
			>
				<Grid container style={{ width: "100%" }}>
					<Grid item xs={12}>
						<Tabs
							orientation={tabsOrientation}
							value={tabValue}
							onChange={handleSetTabValue}
						>
							<Tab
								label="All"
								value={"All"}
								icon={
									<ManageAccountsOutlined fontSize="small" sx={{ mt: -0.25 }} />
								}
							/>
							<Tab
								label="Active"
								value={"Active"}
								icon={<AccessTimeFilled fontSize="small" sx={{ mt: -0.25 }} />}
							/>
							<Tab
								label="Completed"
								value={"Completed"}
								icon={<CheckCircle fontSize="small" sx={{ mt: -0.25 }} />}
							/>
							<Tab
								label="Canceled"
								value={"Canceled"}
								icon={<Cancel fontSize="small" sx={{ mt: -0.25 }} />}
							/>
						</Tabs>
					</Grid>
				</Grid>
			</MDBox>

			{/* Table */}
			<MDBox pt={3}>
				{isLoading ? (
					<MDBox lineHeight={1} textAlign="center" py={3}>
						<MDTypography variant="caption" color="text">Loading...</MDTypography>
					</MDBox>
				) : data && data.length > 0 ? (
					<DataTable
						rows={data}
						columns={columns}
						paginationMode="server"
						serverPagination={{
							total: pagination?.total || 0,
							page: currentPage,
							limit: itemsPerPage,
							onPageChange: handlePageChange
						}}
					/>
				) : (
					<MDBox lineHeight={1} textAlign="center">
						<MDTypography
							display="block"
							variant="caption"
							color="text"
							fontWeight="medium"
							fontSize="20px"
						>
							{error ? error : " No Data Found"}
						</MDTypography>
					</MDBox>
				)}
			</MDBox>
		</Card>
	);
});

/* --------------------
	 Main component
-------------------- */
function Tables({ Token, CheckToken, TableOnly }) {
	const navigate = useNavigate();
	const [data, setData] = useState([]);
	const [error, setError] = useState("");
	const [tabValue, setTabValue] = useState("All");
	const [isLoading, setIsLoading] = useState(false);
	const isFetchingRef = useRef(false);
	const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 0 });
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [filters, setFilters] = useState({ status: "", serviceOffering: "", admin: "", property: "" });
	const [filterOptions, setFilterOptions] = useState({ statuses: [], serviceOfferings: [], admins: [], properties: [] });
	const [isFilterLoading, setIsFilterLoading] = useState(false);
	const [filterError, setFilterError] = useState("");

	/* Columns definition (memoized) */
	const columns = useMemo(() => {
		return [
			{
				headerName: "status",
				field: "status",
				flex: 1,
				renderCell: ({ row: a }) => (
					<MDBadge
						badgeContent={a?.status}
						color={a?.bgColor || "dark"}
						variant="gradient"
						size="sm"
					/>
				),
			},
			{ flex: 1, headerName: "Number", field: "Number" },
			{ headerName: "reason", field: "reason", flex: 1 },
			{ headerName: "description", field: "description", flex: 1 },
			{
				headerName: "Service Offered",
				field: "ServiceOffered",
				flex: 1,
				valueGetter: ({ row: a }) => `${a?.ServiceOffered?.title || "-"}`,
			},
			{
				headerName: "Review",
				field: "Review",
				flex: 1,
				valueGetter: ({ row: a }) => `${a?.Review?._id || "-"}`,
			},
			{
				headerName: "Bill",
				field: "Bill",
				flex: 1,
				valueGetter: ({ row: a }) => `${a?.Bill?.TotalAfterDiscount || "-"}`,
			},
			{
				headerName: "Created At",
				field: "Created At",
				flex: 2,
				valueGetter: ({ row: a }) =>
					`${moment(a?.created_at).format("DD-MM-YYYY hh:mm") || "-"}`,
			},
			{
				headerName: "Time Passed",
				field: "Time Passed",
				flex: 2,
				valueGetter: ({ row: a }) =>
					`${Math.round(daysPassed(a?.created_at, new Date())) + " days" ||
					"-"}`,
			},
			{
				headerName: "Tower No",
				field: "Tower No",
				flex: 1,
				valueGetter: ({ row: a }) => `${a?.Property?.Tower || "-"}`,
			},
			{
				headerName: "Property",
				field: "Property",
				flex: 1,
				valueGetter: ({ row: a }) => `${a?.Property?.description || "-"}`,
			},
			{
				headerName: "Payment",
				field: "Payment",
				flex: 1,
				valueGetter: ({ row: a }) => `${a?.Payment?.Type || "-"}`,
			},
			{
				flex: 1,
				headerName: "Assigned To",
				field: "AssignedTo",
				renderCell: (params) => (
					<div>
						{params?.row?.Tasks?.length > 0 ? (
							<AvatarGroup max={4}>
								{params?.row?.Tasks?.map((a) => (
									<Avatar
										key={a?.assignedTo?._id || Math.random()}
										alt="Assigned"
										src={
											a?.assignedTo?.profilePicture?.filename
												? `${ImageCloud}/${a?.assignedTo?.profilePicture?.filename}`
												: AdminIcon
										}
										sx={{ width: 30, height: 30 }}
									/>
								))}
							</AvatarGroup>
						) : (
							"-"
						)}
					</div>
				),
			},
			{
				headerName: "Action",
				field: "action",
				flex: 1,
				renderCell: ({ row: a }) => (
					<MDBox>
						<MDBadge
							badgeContent={
								a?.Payment?._id
									? "View Service"
									: a?.Bill?._id
										? "Check Bill"
										: "Edit"
							}
							color={"dark"}
							variant="gradient"
							size="sm"
							onClick={() =>
								navigate(`/ServiceAction/${a?._id}/${a?.ServiceOffered?._id}`)
							}
						/>
					</MDBox>
				),
			},
		];
	}, [navigate]);

	/* Fetching data (memoized function) */
	const GetServices = useCallback(() => {
		// Prevent duplicate API calls
		if (isFetchingRef.current) return;
		
		const tokenHeader = Token || localStorage.getItem("accessToken") || localStorage.getItem("token");
		if (tokenHeader) {
			isFetchingRef.current = true;
			setIsLoading(true);
			const params = {
				page: currentPage,
				limit: itemsPerPage
			};
			if (filters.status) params.status = filters.status;
			if (filters.serviceOffering) params.serviceOffering = filters.serviceOffering;
			if (filters.admin) params.admin = filters.admin;
			if (filters.property) params.property = filters.property;
			axios
				.get(`${process.env.REACT_APP_PUBLIC_PATH}/GetAllService`, {
					params,
					headers: { Authorization: `Bearer ${tokenHeader}` },
				})
				.then((res) => {
					if (res?.data?.status === 200) {
						const arr = res?.data?.data
							.filter((a) => {
								const Time =
									(new Date() - new Date(a?.created_at)) / (1000 * 60 * 60);

								if (Time >= 72) a.bgColor = "error";
								else if (Time >= 48) a.bgColor = "warning";
								else if (Time >= 24) a.bgColor = "secondary";
								else a.bgColor = "info";

								if (tabValue === "All") return true;
								else if (
									tabValue === "Active" &&
									(a?.status === "active" ||
										a?.status === "inactive" ||
										a?.status === "delayed")
								)
									return true;
								else if (tabValue === "Completed" && a?.status === "completed") {
									a.bgColor = "success";
									return true;
								} else if (
									tabValue === "Canceled" &&
									(a?.status === "canceled" || a?.status === "canceledByUser")
								) {
									a.bgColor = "error";
									return true;
								}
								return false;
							})
							.sort((a, b) => {
								const timeA =
									(new Date() - new Date(a?.created_at)) / (1000 * 60 * 60);
								const timeB =
									(new Date() - new Date(b?.created_at)) / (1000 * 60 * 60);

								if (timeA >= 72 && timeB >= 72) return timeA - timeB;
								if (timeA >= 48 && timeB >= 48) return timeA - timeB;
								if (timeA >= 24 && timeB >= 24) return timeA - timeB;
								return 0;
							});

						setData(arr);
						if (res?.data?.pagination) {
							setPagination(res?.data?.pagination);
						}
					} else {
						setError(res?.data?.message);
					}
				})
				.catch((err) => setError(err?.message))
				.finally(() => {
					setIsLoading(false);
					isFetchingRef.current = false;
				});
		} else {
			setTimeout(() => {
				CheckToken();
			}, 500);
		}
	}, [Token, tabValue, CheckToken, currentPage, itemsPerPage, filters]);

	/* Memoized callbacks for child component */
	const handleSetTabValue = useCallback((newValue) => {
		setCurrentPage(1);
		setTabValue(newValue);
	}, []);

	const handlePageChange = useCallback((newPage, newPageSize) => {
		if (newPageSize !== itemsPerPage) {
			setItemsPerPage(newPageSize);
			setCurrentPage(1);
		} else {
			setCurrentPage(newPage);
		}
	}, [itemsPerPage]);

	const handleFilterChange = useCallback((field, value) => {
		setCurrentPage(1);
		setFilters((prev) => ({
			...prev,
			[field]: value || ""
		}));
	}, []);

	const handleClearFilters = useCallback(() => {
		setCurrentPage(1);
		setFilters({ status: "", serviceOffering: "", admin: "", property: "" });
	}, []);

	const fetchServiceFilters = useCallback(() => {
		const tokenHeader = Token || localStorage.getItem("accessToken") || localStorage.getItem("token");
		if (!tokenHeader) return;
		setIsFilterLoading(true);
		setFilterError("");
		axios
			.get(`${process.env.REACT_APP_PUBLIC_PATH}/GetServiceFilters`, {
				headers: { Authorization: `Bearer ${tokenHeader}` }
			})
			.then((res) => {
				if (res?.data?.status === 200) {
					setFilterOptions(res?.data?.data || {});
				} else {
					setFilterError(res?.data?.message || "Unable to load filters");
				}
			})
			.catch((err) => {
				setFilterError(err?.response?.data?.message || err?.message || "Unable to load filters");
			})
			.finally(() => setIsFilterLoading(false));
	}, [Token]);

	/* Effects */
	useEffect(() => {
		fetchServiceFilters();
	}, [fetchServiceFilters]);

	useEffect(() => {
		GetServices();
	}, [GetServices]);

	/* Render */
	if (TableOnly) {
		return (
			<ServiceTableComponent
				data={data}
				error={error}
				tabValue={tabValue}
				columns={columns}
				setTabValue={handleSetTabValue}
				navigate={navigate}
				isLoading={isLoading}
				pagination={pagination}
				currentPage={currentPage}
				itemsPerPage={itemsPerPage}
				handlePageChange={handlePageChange}
				filters={filters}
				filterOptions={filterOptions}
				onFilterChange={handleFilterChange}
				onClearFilters={handleClearFilters}
				isFilterLoading={isFilterLoading}
				filterError={filterError}
			/>
		);
	}

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<MDBox pt={6} pb={3}>
				<Grid container spacing={6}>
					<Grid item xs={12}>
					<ServiceTableComponent
						data={data}
						error={error}
						tabValue={tabValue}
						columns={columns}
						setTabValue={handleSetTabValue}
						navigate={navigate}
						isLoading={isLoading}
						pagination={pagination}
						currentPage={currentPage}
						itemsPerPage={itemsPerPage}
						handlePageChange={handlePageChange}
						filters={filters}
						filterOptions={filterOptions}
						onFilterChange={handleFilterChange}
						onClearFilters={handleClearFilters}
						isFilterLoading={isFilterLoading}
						filterError={filterError}
					/>
					</Grid>
				</Grid>
			</MDBox>
		</DashboardLayout>
	);
}

export default withAuthContext(Tables);
